import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { cn } from '@/lib/utils';
import type { Property } from '@/types/api';
import type { PropertySortBy, PropertySortDir } from '../api/properties-api';

interface PropertiesTableProps {
  properties: Property[];
  sortBy: PropertySortBy;
  sortDir: PropertySortDir;
  onSortChange: (next: PropertySortBy) => void;
}

// May 15 batch — list view counterpart to PropertyCard. Sortable columns:
// Name, Buildings, Units, Meters, Active Alerts (and createdAt indirectly).
// Address and Status are decorative columns (Status is derived from the
// alarm count). Click a row to drill into the Property Portal.
export function PropertiesTable({ properties, sortBy, sortDir, onSortChange }: PropertiesTableProps) {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
      <Table density="compact">
        <TableHeader>
          <TableRow>
            <SortableTableHead
              label="Name"
              active={sortBy === 'name'}
              direction={sortDir}
              onClick={() => onSortChange('name')}
            />
            <TableHead>Address</TableHead>
            <SortableTableHead
              label="Buildings"
              active={sortBy === 'buildingCount'}
              direction={sortDir}
              align="right"
              onClick={() => onSortChange('buildingCount')}
            />
            <SortableTableHead
              label="Units"
              active={sortBy === 'unitCount'}
              direction={sortDir}
              align="right"
              onClick={() => onSortChange('unitCount')}
            />
            <SortableTableHead
              label="Meters"
              active={sortBy === 'deviceCount'}
              direction={sortDir}
              align="right"
              onClick={() => onSortChange('deviceCount')}
            />
            <SortableTableHead
              label="Active Alerts"
              active={sortBy === 'activeAlarmCount'}
              direction={sortDir}
              align="right"
              onClick={() => onSortChange('activeAlarmCount')}
            />
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {properties.map((property) => {
            const alarmed = (property.activeAlarmCount ?? 0) > 0;
            return (
              <TableRow
                key={property.id}
                onClick={() => navigate(`/properties/${property.id}`)}
                className={cn(
                  'cursor-pointer',
                  alarmed && 'border-l-[3px] border-l-error-500',
                )}
              >
                <TableCell className="font-medium text-gray-900 dark:text-gray-100">{property.name}</TableCell>
                <TableCell className="text-gray-500 dark:text-gray-400">{property.address}</TableCell>
                <TableCell className="text-right tabular-nums">{property.buildingCount ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">{property.unitCount ?? 0}</TableCell>
                <TableCell className="text-right tabular-nums">{property.deviceCount ?? 0}</TableCell>
                <TableCell
                  className={cn(
                    'text-right tabular-nums',
                    alarmed && 'text-error-700 dark:text-error-400 font-medium',
                  )}
                >
                  {property.activeAlarmCount ?? 0}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      alarmed
                        ? 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400'
                        : 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400',
                    )}
                  >
                    {alarmed ? 'Alarmed' : 'OK'}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
